/**
 * ExecutionChat — the persistent right-side chat of the Execution surface.
 *
 * ONE chat across the whole Execution view (same UX grammar as Build &
 * Monetize cockpits). The legacy `QuestionDrawer` that slid over the panel
 * is GONE; clicking a question or a task in the LoopPanel now opens that
 * topic as a thread INSIDE this chat.
 *
 *   · Persona dropdown (Aria · Lyra · Hermes) in the header.
 *   · Thread tabs at the top: "General" + one chip per opened question /
 *     task. Click → switch thread (each thread keeps its own history).
 *   · Pinned topic banner when the active thread is a question/task.
 *   · When persona = Aria, the recent activity of the other threads is
 *     injected as a brief context block above the founder's message — she
 *     "reads" what Lyra & Hermes have been saying.
 *   · On a question thread, an inline "I have my answer · unlock atoms"
 *     CTA replaces the composer once the founder is ready.
 *
 * No new SSE endpoint, no new backend code: this routes through the same
 * unified `/api/aria/chat` endpoint as CockpitChat (persona-aware).
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Send, Loader2, X, ChevronDown, MessageCircle, Wrench, PenTool, Compass,
  CheckCircle2,
} from 'lucide-react';
import AriaThinkingTrail from '../aria/AriaThinkingTrail';
import ProposalCards from '../aria/ProposalCards';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PERSONA_META = {
  aria:   { name: 'Aria',   role: 'The Architect',   accent: '#7C3AED', soft: '#A78BFA', gradient: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)', Icon: Wrench },
  lyra:   { name: 'Lyra',   role: 'The Storyteller', accent: '#FF6B1A', soft: '#FFD166', gradient: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 100%)', Icon: PenTool },
  hermes: { name: 'Hermes', role: 'The Pathfinder',  accent: '#FFB347', soft: '#FFE89D', gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)', Icon: Compass },
};

const normalizeOwner = (o) => {
  const x = String(o || '').toLowerCase();
  if (['markus', 'marcus', 'revenue', 'sales', 'growth'].includes(x)) return 'hermes';
  if (['builder', 'aria'].includes(x)) return 'aria';
  if (['content', 'lyra'].includes(x)) return 'lyra';
  if (['hermes'].includes(x)) return 'hermes';
  return 'aria';
};

const GENERAL_KEY = 'general';

const emptyThread = (persona, topic = null) => ({
  persona,
  topic,                       // { kind: 'question'|'task', data: {...} } or null
  messages: [],
  unread: false,
});

/* ---------------- public imperative API (used by LoopPanel callbacks) ----------------
   `openTopic` lets the parent push a question or task into the chat as
   a new (or existing) thread. We rely on a forwarded ref so the parent
   doesn't have to mirror our state. */
const ExecutionChat = React.forwardRef(function ExecutionChat({ projectId, loopId, onAnswerSubmitted }, ref) {
  // ONE unified thread for all personas (ChatGPT style). Every message is
  // labelled with its author (founder / aria / lyra / hermes) so the
  // conversation reads linearly even when the founder switches persona.
  const storageKey = projectId ? `execution-chat:${projectId}` : null;

  // Robust loader — supports the legacy multi-thread shape so we don't
  // wipe conversations the founder may have started before the refactor.
  const loadSnapshot = () => {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (Array.isArray(p?.messages)) return p;          // new shape
      if (p?.threads?.[GENERAL_KEY]) {                   // legacy: hoist the General thread
        const gen = p.threads[GENERAL_KEY];
        return { messages: gen.messages || [], currentPersona: gen.persona || 'aria', pinnedTopic: null };
      }
    } catch { /* corrupted snapshot */ }
    return null;
  };

  const initial = loadSnapshot() || { messages: [], currentPersona: 'aria', pinnedTopic: null };
  const [messages, setMessages] = useState(initial.messages);
  const [currentPersona, setCurrentPersona] = useState(initial.currentPersona);
  const [pinnedTopic, setPinnedTopic] = useState(initial.pinnedTopic);
  const [serverHydrated, setServerHydrated] = useState(false);

  // ── Hydrate from Mongo on mount (server is source of truth · cross-device).
  // localStorage above gave us instant first paint; this catches up.
  useEffect(() => {
    if (!projectId) return;
    let live = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/aria/projects/${projectId}/chat/thread`, { credentials: 'include' });
        if (!r.ok) { if (live) setServerHydrated(true); return; }
        const d = await r.json();
        if (!live) return;
        // Only overwrite local state if server has MORE messages — protects
        // against losing an in-flight reply the user just typed.
        const serverMsgs = Array.isArray(d.messages) ? d.messages : [];
        setMessages(prev => serverMsgs.length >= prev.length ? serverMsgs : prev);
        if (d.current_persona) setCurrentPersona(d.current_persona);
        if (d.pinned_topic !== undefined) setPinnedTopic(d.pinned_topic);
        setServerHydrated(true);
      } catch {
        if (live) setServerHydrated(true);
      }
    })();
    return () => { live = false; };
  }, [projectId]);

  // ── Debounced persist to Mongo (500ms). localStorage keeps writing too for offline.
  // When a pending run is in flight we flush IMMEDIATELY (debounce → 0ms) so a
  // founder who navigates away within the debounce window never loses the
  // pending message — the poller can rehydrate it from Mongo on remount.
  const persistRef = useRef(null);
  const hasPendingRun = messages.some(m => m.role === 'agent' && m.pending_run_id);
  useEffect(() => {
    if (!projectId || !serverHydrated) return;
    if (persistRef.current) clearTimeout(persistRef.current);
    const delay = hasPendingRun ? 0 : 500;
    persistRef.current = setTimeout(() => {
      fetch(`${API}/api/aria/projects/${projectId}/chat/thread`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, current_persona: currentPersona, pinned_topic: pinnedTopic }),
      }).catch(() => { /* silent — localStorage is the fallback */ });
    }, delay);
    return () => { if (persistRef.current) clearTimeout(persistRef.current); };
  }, [projectId, serverHydrated, messages, currentPersona, pinnedTopic, hasPendingRun]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify({ messages, currentPersona, pinnedTopic }));
    } catch { /* quota */ }
  }, [storageKey, messages, currentPersona, pinnedTopic]);

  const [text, setText] = useState('');
  const [lastTrail, setLastTrail] = useState(null);
  const [showPersonaMenu, setShowPersonaMenu] = useState(false);
  const [showAnswerStep, setShowAnswerStep] = useState(false);
  const [finalAnswer, setFinalAnswer] = useState('');
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const scrollerRef = useRef(null);
  const textareaRef = useRef(null);

  // `thinking` is now DERIVED from the chat history: any agent message that
  // still has a pending_run_id means a background run is in flight. This is
  // what makes the UX navigation-proof — the indicator survives unmount /
  // remount because it lives in the persisted message stream.
  const thinking = useMemo(
    () => messages.some(m => m.role === 'agent' && m.pending_run_id),
    [messages]
  );

  const meta = PERSONA_META[currentPersona] || PERSONA_META.aria;

  /* Expose the open/close API to the parent. */
  React.useImperativeHandle(ref, () => ({
    openQuestion(question) {
      if (!question) return;
      setPinnedTopic({ kind: 'question', data: question });
      setCurrentPersona(normalizeOwner(question.asked_by));
      setShowAnswerStep(false);
    },
    openTask(task) {
      if (!task) return;
      setPinnedTopic({ kind: 'task', data: task });
      setCurrentPersona(normalizeOwner(task.owner_agent_id));
      setShowAnswerStep(false);
    },
    openGeneral() {
      setPinnedTopic(null);
      setShowAnswerStep(false);
    },
  }), []);

  /* Auto-scroll on every change in the conversation. */
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages.length, thinking, lastTrail]);

  /* ──────────────────────────────────────────────────────────────────────
     Pending-run poller — the heart of the navigation-proof UX.

     For every message that still carries a `pending_run_id`, poll the
     server every 2s. When the run finishes, mutate the message in place
     (replace `pending_run_id` with the actual reply + trail + proposals).
     The standard PUT debounce then persists the final state to Mongo.

     This effect is keyed on the set of pending run_ids (not the whole
     messages array) so we don't restart timers on every keystroke.
     A `pollersRef` map tracks active intervals so we never double-poll
     the same run, and we clean up on unmount.
     ──────────────────────────────────────────────────────────────── */
  const pollersRef = useRef(new Map());
  const pendingRunIdsKey = useMemo(
    () => messages
      .filter(m => m.role === 'agent' && m.pending_run_id)
      .map(m => m.pending_run_id)
      .join('|'),
    [messages]
  );
  useEffect(() => {
    const activeIds = new Set(
      messages.filter(m => m.role === 'agent' && m.pending_run_id).map(m => m.pending_run_id)
    );
    // Stop pollers for runs that no longer exist.
    for (const [rid, timer] of pollersRef.current.entries()) {
      if (!activeIds.has(rid)) {
        clearInterval(timer);
        pollersRef.current.delete(rid);
      }
    }
    // Start a poller for each new pending run.
    for (const runId of activeIds) {
      if (pollersRef.current.has(runId)) continue;
      const poll = async () => {
        try {
          const r = await fetch(`${API}/api/aria/chat/runs/${runId}`, { credentials: 'include' });
          if (!r.ok) return;
          const d = await r.json();
          if (d.status === 'pending') return;
          // Terminal state — fold result back into the message.
          setMessages(prev => prev.map(m => {
            if (m.pending_run_id !== runId) return m;
            if (d.status === 'done') {
              return {
                role: 'agent',
                agent_id: m.agent_id,
                content: d.reply || '',
                at: d.completed_at || new Date().toISOString(),
                trail: d.trail || null,
                proposals: d.applied_changes || [],
              };
            }
            // error
            return {
              role: 'system',
              content: `${PERSONA_META[m.agent_id]?.name || 'Agent'} couldn't respond (${d.error || 'unknown'}). Try again.`,
              at: d.completed_at || new Date().toISOString(),
            };
          }));
        } catch { /* network blip — next tick will retry */ }
      };
      poll();                                    // immediate first hit
      const timer = setInterval(poll, 2000);
      pollersRef.current.set(runId, timer);
    }
  }, [pendingRunIdsKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup any leftover timers when the component truly unmounts.
  useEffect(() => () => {
    for (const timer of pollersRef.current.values()) clearInterval(timer);
    pollersRef.current.clear();
  }, []);

  const send = async (overrideText) => {
    const raw = (overrideText ?? text).trim();
    if (!raw || !projectId || thinking) return;
    const now = new Date().toISOString();

    // Frame the message:
    //   · If a topic is pinned, surface it so the agent has the context.
    //   · Send the last 12 turns of THIS shared thread as history (Aria sees
    //     everyone's exchanges since this is one unified conversation).
    let framed = '';
    if (pinnedTopic?.kind === 'question') framed += `[PINNED QUESTION] ${pinnedTopic.data.question || ''}\n\n`;
    if (pinnedTopic?.kind === 'task')     framed += `[PINNED TASK] ${pinnedTopic.data.title || ''} (state=${pinnedTopic.data.current_state || 'backlog'})\n\n`;
    framed += `[FOUNDER] ${raw}`;

    const history = messages
      .filter(m => m.role === 'user' || m.role === 'agent')
      .slice(-12)
      .map(m => ({ role: m.role === 'agent' ? 'agent' : 'user', content: m.content }));

    setText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setLastTrail(null);

    // Append the founder's message immediately.
    setMessages(prev => [...prev, { role: 'user', content: raw, at: now }]);

    // Kick off a background run on the server. We get a run_id back; the
    // poller (effect below) takes over from here. The reply will land even
    // if the founder navigates to Office or closes the tab.
    try {
      const r = await fetch(`${API}/api/aria/chat/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          persona: currentPersona,
          surface: 'execution',
          message: framed,
          history,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      const runId = d.run_id;
      // Insert a pending agent message. The persisted `pending_run_id` is
      // what allows ANY future remount (navigation, reload, other device)
      // to pick up the in-flight run and finish it gracefully.
      setMessages(prev => [...prev, {
        role: 'agent',
        agent_id: currentPersona,
        content: '',
        at: new Date().toISOString(),
        pending_run_id: runId,
      }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Network error · ${e?.message || e}`,
        at: new Date().toISOString(),
      }]);
    }
  };

  /* Submit the final answer for a pinned question → unlocks the blocked atoms. */
  const submitFinalAnswer = async () => {
    if (!finalAnswer.trim() || submittingAnswer) return;
    if (pinnedTopic?.kind !== 'question' || !loopId) return;
    setSubmittingAnswer(true);
    try {
      const r = await fetch(`${API}/api/loops/${loopId}/questions/${pinnedTopic.data.question_id}/answer`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: finalAnswer.trim() }),
      });
      if (r.ok) {
        setShowAnswerStep(false);
        setFinalAnswer('');
        setPinnedTopic(null);
        onAnswerSubmitted?.();
      }
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const isQuestionThread = pinnedTopic?.kind === 'question';
  const hasUserSent = messages.some(m => m.role === 'user');

  return (
    <aside data-testid="execution-chat" style={{
      width: 400, flexShrink: 0, height: '100%',
      background: '#FFFFFF',
      borderLeft: '1px solid rgba(15,15,30,0.08)',
      display: 'flex', flexDirection: 'column',
      color: '#0F0F1A',
    }}>
      {/* ── Header with persona switcher ── */}
      <div style={{
        padding: '14px 18px', borderBottom: '1px solid rgba(15,15,30,0.06)',
        background: '#FBFAF7', position: 'relative',
      }}>
        <button
          data-testid="persona-switch-btn"
          onClick={() => setShowPersonaMenu(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: meta.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <meta.Icon size={16} color="#FFFFFF" />
          </div>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#0F0F1A', letterSpacing: '-0.01em' }}>
              {meta.name} · {meta.role}
            </div>
            <div style={{
              fontSize: 9.5, color: '#6B7280', letterSpacing: '0.06em', marginTop: 2,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <span style={{
                width: 5, height: 5, borderRadius: 999,
                background: '#10B981', boxShadow: '0 0 6px #10B981',
              }} />
              ARIA · LYRA · HERMES IN THE ROOM
            </div>
          </div>
          <ChevronDown size={14} color="#6B7280" style={{
            transition: 'transform 0.2s', transform: showPersonaMenu ? 'rotate(180deg)' : 'rotate(0)',
          }} />
        </button>

        {showPersonaMenu && (
          <div data-testid="persona-menu" style={{
            position: 'absolute', top: '100%', left: 14, right: 14,
            background: '#FFFFFF', border: '1px solid rgba(15,15,30,0.1)',
            borderRadius: 12, boxShadow: '0 12px 32px rgba(15,15,30,0.12)',
            zIndex: 20, padding: 6, marginTop: 6,
          }}>
            {Object.entries(PERSONA_META).map(([key, m]) => (
              <button
                key={key}
                data-testid={`persona-option-${key}`}
                onClick={() => {
                  setCurrentPersona(key);
                  setShowPersonaMenu(false);
                }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', borderRadius: 8,
                  background: key === currentPersona ? `${m.soft}26` : 'transparent',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={e => e.currentTarget.style.background = `${m.soft}30`}
                onMouseLeave={e => e.currentTarget.style.background = key === currentPersona ? `${m.soft}26` : 'transparent'}
              >
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: m.gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <m.Icon size={12} color="#FFFFFF" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F1A' }}>{m.name}</div>
                  <div style={{ fontSize: 10, color: '#6B7280' }}>{m.role}</div>
                </div>
                {key === currentPersona && <CheckCircle2 size={13} color={m.accent} />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Pinned topic banner (question or task in focus) ── */}
      {pinnedTopic && (
        <div style={{ position: 'relative' }}>
          <PinnedTopic topic={pinnedTopic} accent={meta.accent} />
          <button
            data-testid="execution-chat-clear-pin"
            onClick={() => setPinnedTopic(null)}
            title="Clear pinned topic"
            style={{
              position: 'absolute', top: 16, right: 24,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#9CA3AF', padding: 4, fontSize: 16, lineHeight: 1,
            }}
          >×</button>
        </div>
      )}

      {/* ── Aria-in-the-room hint (when current persona isn't Aria) ── */}
      {currentPersona !== 'aria' && (
        <div
          data-testid="aria-in-room-hint"
          onClick={() => setCurrentPersona('aria')}
          style={{
            padding: '6px 18px', background: '#F5F3FF',
            borderBottom: '1px solid #E0DAFF',
            display: 'flex', alignItems: 'center', gap: 8,
            cursor: 'pointer',
          }}
          title="Switch to Aria — she reads this conversation."
        >
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#7C3AED', boxShadow: '0 0 6px #7C3AED',
          }} />
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: '#7C3AED',
          }}>Aria is listening</span>
          <span style={{ fontSize: 10.5, color: '#6B7280' }}>
            · tap to bring her into the conversation
          </span>
        </div>
      )}

      {/* ── Unified conversation (linear, ChatGPT-style) ── */}
      <div ref={scrollerRef} data-testid="execution-chat-history" style={{
        flex: 1, overflowY: 'auto', padding: '14px 18px',
        display: 'flex', flexDirection: 'column', gap: 10,
        background: '#FFFFFF',
      }}>
        {messages.length === 0 && !thinking && (
          <div style={{
            margin: 'auto', textAlign: 'center', padding: '24px 18px',
            color: '#9CA3AF', fontSize: 12, lineHeight: 1.55, fontStyle: 'italic',
          }}>
            {pinnedTopic
              ? `Talk to ${meta.name} about this ${pinnedTopic.kind}. Full venture context loaded.`
              : `Talk to ${meta.name} · she sees the code, the plan, all open threads.`}
          </div>
        )}
        {messages.map((m, i) => {
          // Pending agent messages are placeholders for the poller; the
          // live "is reading the venture…" indicator below renders the
          // thinking UI instead, so we hide the empty bubble.
          if (m.role === 'agent' && m.pending_run_id) return null;
          return (
            <MessageBubble
              key={i}
              msg={m}
              personaMeta={PERSONA_META[m.agent_id] || meta}
            />
          );
        })}
        {thinking && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lastTrail && lastTrail.length > 0 && (
              <AriaThinkingTrail trail={lastTrail} pulsing compact />
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px' }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', background: meta.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Loader2 size={10} color="#FFFFFF" style={{ animation: 'spin 0.9s linear infinite' }} />
              </div>
              <span style={{ fontSize: 11, color: '#6B7280', fontStyle: 'italic' }}>
                {meta.name} is reading the venture…
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Composer  OR  Final-answer step (question threads only) ── */}
      {!showAnswerStep && (
        <div style={{
          padding: '12px 14px 16px', borderTop: '1px solid rgba(15,15,30,0.06)',
          background: '#FBFAF7',
        }}>
          {/* ChatGPT-style composer: roomy multi-line textarea with the send
              button anchored bottom-right INSIDE the box. Grows with content
              up to 200px, then scrolls. */}
          <div style={{
            position: 'relative',
            background: '#FFFFFF',
            border: '1px solid rgba(15,15,30,0.12)',
            borderRadius: 20,
            boxShadow: '0 1px 2px rgba(15,15,30,0.04)',
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = meta.accent;
              e.currentTarget.style.boxShadow = `0 0 0 3px ${meta.soft}30`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(15,15,30,0.12)';
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,15,30,0.04)';
            }}
          >
            <textarea
              ref={textareaRef}
              data-testid="execution-chat-input"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                // Auto-grow: reset then expand to scrollHeight, capped at 200px.
                const ta = e.currentTarget;
                ta.style.height = 'auto';
                ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder={`Message ${meta.name}…`}
              rows={2}
              disabled={thinking}
              style={{
                width: '100%',
                minHeight: 56,
                maxHeight: 200,
                padding: '14px 56px 14px 16px',
                background: 'transparent', border: 'none', outline: 'none',
                resize: 'none', fontFamily: 'inherit',
                fontSize: 14, lineHeight: 1.55,
                color: '#0F0F1A',
                caretColor: meta.accent,
                boxSizing: 'border-box',
              }}
            />
            <button
              data-testid="execution-chat-send"
              onClick={() => send()}
              disabled={!text.trim() || thinking}
              style={{
                position: 'absolute', right: 8, bottom: 8,
                width: 36, height: 36,
                background: !text.trim() || thinking ? '#E5E7EB' : meta.gradient,
                color: '#FFFFFF', border: 'none', borderRadius: '50%',
                cursor: thinking ? 'wait' : (!text.trim() ? 'default' : 'pointer'),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.12s, background 0.12s',
              }}
              onMouseDown={(e) => { if (text.trim() && !thinking) e.currentTarget.style.transform = 'scale(0.92)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {thinking
                ? <Loader2 size={15} style={{ animation: 'spin 0.7s linear infinite' }} />
                : <Send size={15} />}
            </button>
          </div>
          <div style={{
            marginTop: 6, fontSize: 10, color: '#9CA3AF',
            letterSpacing: '0.04em', textAlign: 'right',
          }}>
            ↵ to send · ⇧↵ for newline
          </div>

          {/* Question pinned → CTA to commit the final answer once founder is ready. */}
          {isQuestionThread && hasUserSent && (
            <button
              data-testid="execution-chat-answer-btn"
              onClick={() => {
                const lastUser = [...messages].reverse().find(m => m.role === 'user');
                setFinalAnswer(lastUser?.content || '');
                setShowAnswerStep(true);
              }}
              style={{
                width: '100%', marginTop: 8,
                padding: '8px 12px', borderRadius: 999,
                background: '#FFFFFF', color: meta.accent,
                fontWeight: 700, fontSize: 10,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                border: `1px solid ${meta.accent}`, cursor: 'pointer',
              }}
            >
              I have my answer · unlock atoms →
            </button>
          )}
        </div>
      )}

      {showAnswerStep && isQuestionThread && (
        <div style={{
          padding: 14, borderTop: `2px solid ${meta.accent}`,
          background: '#FBFAF7',
        }}>
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: meta.accent, marginBottom: 6,
          }}>Final answer · unlocks {pinnedTopic?.data?.blocks_task_ids?.length || 0} atom(s)</div>
          <textarea
            data-testid="execution-chat-answer-input"
            value={finalAnswer}
            onChange={e => setFinalAnswer(e.target.value)}
            placeholder="Your decision…"
            rows={3}
            style={{
              width: '100%', padding: 10, borderRadius: 10,
              border: '1px solid rgba(15,15,30,0.12)',
              fontSize: 12, fontFamily: 'inherit', resize: 'none',
              background: '#FFFFFF',
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={() => setShowAnswerStep(false)}
              style={{
                flex: 1, padding: '9px 14px', borderRadius: 10,
                background: 'transparent', color: '#6B7280',
                fontWeight: 700, fontSize: 11,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                border: '1px solid rgba(15,15,30,0.12)', cursor: 'pointer',
              }}
            >
              ← Keep discussing
            </button>
            <button
              data-testid="execution-chat-answer-submit"
              onClick={submitFinalAnswer}
              disabled={!finalAnswer.trim() || submittingAnswer}
              style={{
                flex: 2, padding: '9px 14px', borderRadius: 10,
                background: !finalAnswer.trim() || submittingAnswer ? '#9CA3AF' : meta.gradient,
                color: '#FFFFFF', fontWeight: 700, fontSize: 11,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                border: 'none',
                cursor: !finalAnswer.trim() || submittingAnswer ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {submittingAnswer
                ? <Loader2 size={13} style={{ animation: 'spin 0.9s linear infinite' }} />
                : <Send size={13} />}
              {submittingAnswer ? 'Submitting…' : 'Confirm & unlock'}
            </button>
          </div>
        </div>
      )}
    </aside>
  );
});

export default ExecutionChat;

/* ============================== sub-components ============================== */

function ThreadTabs({ threads, activeKey, onSelect, onClose }) {
  const entries = Object.entries(threads);
  if (entries.length <= 1) return null; // only General → no tabs needed
  return (
    <div data-testid="execution-chat-tabs" style={{
      display: 'flex', gap: 6, padding: '8px 12px',
      borderBottom: '1px solid rgba(15,15,30,0.04)',
      background: '#FBFAF7', overflowX: 'auto', flexShrink: 0,
    }}>
      {entries.map(([key, t]) => {
        const isActive = key === activeKey;
        const personaMeta = PERSONA_META[t.persona] || PERSONA_META.aria;
        const label = key === GENERAL_KEY
          ? 'General'
          : t.topic?.kind === 'question'
            ? `${personaMeta.name} · ${(t.topic.data.question || 'Q').slice(0, 24)}…`
            : t.topic?.kind === 'task'
              ? `${personaMeta.name} · ${(t.topic.data.title || 'Task').slice(0, 24)}…`
              : personaMeta.name;
        return (
          <div
            key={key}
            data-testid={`execution-chat-tab-${key}`}
            onClick={() => onSelect(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: 999,
              background: isActive ? personaMeta.accent : '#FFFFFF',
              color: isActive ? '#FFFFFF' : personaMeta.accent,
              border: `1px solid ${isActive ? personaMeta.accent : personaMeta.soft + '60'}`,
              cursor: 'pointer', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.06em', whiteSpace: 'nowrap',
              transition: 'all 0.15s',
            }}
          >
            <span>{label}</span>
            {key !== GENERAL_KEY && (
              <button
                onClick={(e) => { e.stopPropagation(); onClose(key); }}
                data-testid={`execution-chat-tab-close-${key}`}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: 0, display: 'flex', alignItems: 'center',
                  color: isActive ? '#FFFFFF' : personaMeta.accent, opacity: 0.7,
                }}
              >
                <X size={11} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PinnedTopic({ topic, accent }) {
  const isQ = topic.kind === 'question';
  const title = isQ ? topic.data.question : topic.data.title;
  const sub = isQ
    ? topic.data.why
    : `${(topic.data.priority || 'P1')} · ${topic.data.current_state || 'backlog'} · ${topic.data.eta_minutes || 30}min`;
  return (
    <div data-testid="execution-chat-pinned" style={{
      padding: '10px 18px', background: '#FBFAF7',
      borderBottom: '1px solid rgba(15,15,30,0.04)',
    }}>
      <div style={{
        padding: 10, background: '#FFFFFF', borderRadius: 10,
        borderLeft: `3px solid ${accent}`,
      }}>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.16em',
          color: accent, textTransform: 'uppercase', marginBottom: 4,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <MessageCircle size={10} /> {isQ ? 'Pinned question' : 'Pinned task'}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#0F0F1A', lineHeight: 1.4 }}>
          {title}
        </div>
        {sub && (
          <div style={{ fontSize: 10.5, color: '#6B7280', marginTop: 4, fontStyle: 'italic' }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

/* Parse `**Aria's note** — …`, `**Lyra's add** — …`, `**Hermes' add** — …`
   blocks out of the agent's reply so we can render them as colored
   consultation chips INSTEAD of leaving the markdown raw. This is what makes
   the panel mode visible: the founder sees who chimed in at a glance.

   Returns { mainBody, contributions: [{agent, text}] }. */
function parseConsultations(raw) {
  if (!raw || typeof raw !== 'string') return { mainBody: raw || '', contributions: [] };
  // Match  **Aria's note** — …   /   **Lyra's add** — …   /   **Hermes' add** — …
  // (curly quotes too). The body can span multiple lines until the next
  // matching marker or the end of the message.
  const RX = /\*\*(Aria|Lyra|Hermes)['']s\s+(?:note|add)\*\*\s*[—\-:]\s*([\s\S]*?)(?=\n\s*\*\*(?:Aria|Lyra|Hermes)['']s\s+(?:note|add)\*\*|$)/gi;
  const contributions = [];
  const indices = [];
  let m;
  while ((m = RX.exec(raw)) !== null) {
    contributions.push({ agent: m[1].toLowerCase(), text: m[2].trim() });
    indices.push([m.index, RX.lastIndex]);
  }
  if (!indices.length) return { mainBody: raw, contributions: [] };
  // Strip the matched markdown out of the main body.
  let mainBody = '';
  let cursor = 0;
  for (const [s, e] of indices) {
    mainBody += raw.slice(cursor, s);
    cursor = e;
  }
  mainBody += raw.slice(cursor);
  return { mainBody: mainBody.trim().replace(/\n{3,}/g, '\n\n'), contributions };
}

function ConsultationChips({ contributions, defaultAccent }) {
  if (!contributions || !contributions.length) return null;
  return (
    <div data-testid="consultation-chips" style={{
      display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8,
    }}>
      {contributions.map((c, i) => {
        const meta = PERSONA_META[c.agent] || { accent: defaultAccent, soft: defaultAccent, name: c.agent };
        const Icon = meta.Icon || MessageCircle;
        return (
          <div key={i} data-testid={`chip-${c.agent}`} style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            padding: '7px 10px',
            background: `${meta.soft}1A`,
            border: `1px solid ${meta.soft}55`,
            borderLeft: `3px solid ${meta.accent}`,
            borderRadius: 8,
          }}>
            <div style={{
              width: 18, height: 18, borderRadius: '50%',
              background: meta.gradient || meta.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: 1,
            }}>
              <Icon size={9} color="#FFFFFF" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 8.5, fontWeight: 800, color: meta.accent,
                letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 2,
              }}>
                {meta.name} chimed in
              </div>
              <div style={{ fontSize: 11.5, color: '#0F0F1A', lineHeight: 1.5 }}>
                {c.text}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MessageBubble({ msg, personaMeta }) {
  if (msg.role === 'system') {
    return (
      <div style={{
        padding: '8px 11px', background: '#FEF2F2', color: '#B91C1C',
        border: '1px solid #FECACA', borderRadius: 10, fontSize: 11.5,
      }}>
        {msg.content}
      </div>
    );
  }
  const isUser = msg.role === 'user';
  const { mainBody, contributions } = !isUser
    ? parseConsultations(msg.content)
    : { mainBody: msg.content, contributions: [] };
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: isUser ? 'flex-end' : 'flex-start', gap: 4,
    }}>
      {!isUser && msg.trail && (
        <div style={{ width: '90%' }}>
          <AriaThinkingTrail trail={msg.trail} pulsing={false} compact />
        </div>
      )}
      <div style={{
        maxWidth: '85%',
        padding: '9px 12px',
        background: isUser ? `${personaMeta.soft}26` : '#FBFAF7',
        border: isUser ? `1px solid ${personaMeta.soft}50` : '1px solid rgba(15,15,30,0.06)',
        borderRadius: 12,
        borderTopRightRadius: isUser ? 4 : 12,
        borderTopLeftRadius: !isUser ? 4 : 12,
        fontSize: 12, lineHeight: 1.55, color: '#0F0F1A',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {!isUser && (
          <div style={{
            fontSize: 9, fontWeight: 800, color: personaMeta.accent,
            letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 4,
          }}>{personaMeta.name}</div>
        )}
        {mainBody}
      </div>
      {!isUser && contributions.length > 0 && (
        <div style={{ width: '90%' }}>
          <ConsultationChips contributions={contributions} defaultAccent={personaMeta.accent} />
        </div>
      )}
      {!isUser && Array.isArray(msg.proposals) && msg.proposals.length > 0 && (
        <div style={{ width: '90%' }}>
          <ProposalCards proposals={msg.proposals} accent={personaMeta.accent} />
        </div>
      )}
    </div>
  );
}
